import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  Box,
  Chip,
  LinearProgress,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CloudSync as CloudSyncIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Folder as FolderIcon,
  Storage as StorageIcon,
  Sync as SyncIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { InputAdornment } from '@mui/material';
import { dropboxService } from '../../services/dropboxService';
import api from '../../config/axiosConfig';

const ConfiguracionDropbox = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [configuring, setConfiguring] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [authorizationUrl, setAuthorizationUrl] = useState('');
  const [authorizationCode, setAuthorizationCode] = useState('');
  const [showAppSecret, setShowAppSecret] = useState(false);
  const [autoProcessing, setAutoProcessing] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [showTokenConfig, setShowTokenConfig] = useState(false);
  const [showCurrentToken, setShowCurrentToken] = useState(false);
  const [currentConfig, setCurrentConfig] = useState(null);
  
  const [dropboxConfig, setDropboxConfig] = useState({
    id: null,
    urlToken: 'https://api.dropboxapi.com/oauth2/token',
    nombreApp: 'conem_folder',
    activo: false,
    appKey: 'tfi5tqt6vyqgj3y',
    appSecret: 'uzuxet9jg1huft3',
    accessToken: '', // Campo principal para el access token de la BD
    automaticSave: true,
    basePath: '/CONEM_Documents',
    rootFolder: '/CONEM_Documents',
    pipBasePath: 'C:/Users/' + (window.navigator.userAgent.includes('Windows') ? process.env.USERNAME || 'user' : 'user') + '/Dropbox/CONEM_PIPC',
    useDropboxPath: true,
    autoSync: true,
    syncFrequency: 'daily',
    maxFileSize: '100',
    allowedFileTypes: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'png']
  });

  const [syncSettings, setSyncSettings] = useState({
    syncQuotations: true,
    syncReports: true,
    syncUserDocuments: false,
    syncSystemBackups: true,
    deleteLocalAfterSync: false,
    compressBeforeSync: true
  });

  const [storageInfo, setStorageInfo] = useState({
    totalSpace: '',
    usedSpace: '',
    availableSpace: '',
    filesCount: 0,
    lastSync: ''
  });

  const [recentFiles, setRecentFiles] = useState([]);
  const [folderStructure, setFolderStructure] = useState({ folders: [], basePath: '' });
  const [loadingStructure, setLoadingStructure] = useState(false);

  const loadFolderStructure = async () => {
    console.log('🔍 Iniciando carga de estructura de carpetas...');
    setLoadingStructure(true);
    try {
      const response = await api.get('/api/dropbox/folder-structure');
      console.log('📦 Respuesta recibida:', response.data);
      console.log('✅ Success:', response.data.success);
      console.log('📁 Base Path:', response.data.basePath);
      console.log('📂 Folders count:', response.data.folders?.length || 0);
      console.log('📂 Folders:', response.data.folders);
      
      if (response.data.success) {
        const newStructure = {
          folders: response.data.folders || [],
          basePath: response.data.basePath || '/'
        };
        console.log('💾 Guardando estructura:', newStructure);
        setFolderStructure(newStructure);
      } else {
        console.warn('⚠️ No se pudo cargar estructura:', response.data.message);
        alert('Error: ' + response.data.message);
      }
    } catch (error) {
      console.error('❌ Error cargando estructura de carpetas:', error);
      console.error('Error details:', error.response?.data);
      alert('Error al cargar estructura: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoadingStructure(false);
      console.log('✅ Carga finalizada');
    }
  };

  const [fieldValidationCheck, setFieldValidationCheck] = useState(false);
  const [savingFieldValidationCheck, setSavingFieldValidationCheck] = useState(false);

  useEffect(() => {
    api.get('/dropbox/field-validation-check')
      .then(res => setFieldValidationCheck(Boolean(res.data?.enabled)))
      .catch(() => {});
  }, []);

  const handleFieldValidationCheckChange = async (enabled) => {
    setSavingFieldValidationCheck(true);
    setError('');
    setSuccess('');
    try {
      const res = await api.put('/dropbox/field-validation-check', { enabled });
      setFieldValidationCheck(Boolean(res.data?.enabled));
      setSuccess(enabled
        ? 'Validar Campos verificará los archivos contra Dropbox.'
        : 'Validar Campos ya no consultará Dropbox (usa el botón "Verificar contra Dropbox" cuando lo necesites).');
    } catch (err) {
      setError(`No se pudo guardar la configuración: ${err.response?.data?.error || err.message}`);
    } finally {
      setSavingFieldValidationCheck(false);
    }
  };

  useEffect(() => {
    loadConfiguration();
    // Load PIP base path from localStorage
    const savedPipPath = localStorage.getItem('conem_pip_base_path');
    if (savedPipPath) {
      setDropboxConfig(prev => ({
        ...prev,
        pipBasePath: savedPipPath
      }));
    }
    
    // Detectar código OAuth en la URL
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    
    if (code && !autoProcessing) {
      console.log('🔐 Código OAuth detectado en URL:', code.substring(0, 20) + '...');
      setAutoProcessing(true);
      setAuthorizationCode(code);
      
      // Procesar automáticamente el código
      setTimeout(() => {
        processOAuthCode(code);
      }, 500);
      
      // Limpiar URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Intentar obtener la configuración activa completa primero
      let response;
      try {
        response = await dropboxService.getActiveConfig();
        console.log('📋 Usando configuración activa completa:', response);
      } catch (activeError) {
        console.warn('⚠️ No se pudo obtener configuración activa, usando método estándar:', activeError);
        response = await dropboxService.getConfig();
      }
      
      if (response) {
        setDropboxConfig(prev => ({
          ...prev,
          id: response.id || prev.id,
          urlToken: response.urlToken || prev.urlToken,
          nombreApp: response.nombreApp || response.appName || prev.nombreApp,
          activo: response.activo !== undefined ? response.activo : prev.activo,
          appKey: response.appKey || prev.appKey,
          appSecret: response.appSecret || prev.appSecret,
          accessToken: response.accessToken || '', // Mostrar token completo desde BD
          automaticSave: response.automaticSave !== undefined ? response.automaticSave : prev.automaticSave,
          basePath: response.basePath || prev.basePath,
          rootFolder: response.rootFolder || prev.rootFolder,
          autoSync: response.automaticSave !== undefined ? response.automaticSave : prev.autoSync
        }));
        
        console.log('🔑 Token cargado desde BD:', response.accessToken ? `${response.accessToken.length} caracteres` : 'Sin token en BD');
        
        // Save to localStorage as fallback
        if (response.basePath) {
          localStorage.setItem('dropbox_base_path', response.basePath);
        }
        if (response.automaticSave !== undefined) {
          localStorage.setItem('dropbox_auto_save', response.automaticSave);
        }
        
        // Actualizar estado de conexión con logging
        const newStatus = response.connected ? 'connected' : 'disconnected';
        console.log(`🔌 Estado de conexión Dropbox: ${newStatus}`, {
          connected: response.connected,
          hasToken: !!response.accessToken,
          tokenLength: response.accessToken?.length
        });
        setConnectionStatus(newStatus);
        
        if (response.connected) {
          try {
            const storageData = await dropboxService.getStorageInfo();
            setStorageInfo(storageData);
            
            const filesData = await dropboxService.getRecentFiles();
            setRecentFiles(filesData);
          } catch (err) {
            console.log('Error al cargar datos adicionales:', err);
          }
        }
      }
    } catch (err) {
      console.error('Error al cargar configuración:', err);
      setError('Error al cargar la configuración de Dropbox');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (field, value) => {
    setDropboxConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSyncSettingsChange = (field, value) => {
    setSyncSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const testConnection = async () => {
    try {
      setConnectionStatus('connecting');
      setError('');
      setSuccess('');
      
      const response = await dropboxService.testConnection();
      
      if (response.success) {
        setConnectionStatus('connected');
        setSuccess('✅ Conexión con Dropbox establecida exitosamente. Listo para subir archivos.');
      } else {
        setConnectionStatus('disconnected');
        setError('❌ Error al conectar con Dropbox. Verifica tus credenciales.');
      }
    } catch (err) {
      setConnectionStatus('disconnected');
      if (err.response && err.response.status === 401) {
        setError('❌ Token de acceso expirado. Es necesario reconfigurar Dropbox.');
      } else {
        setError('❌ Error al conectar con Dropbox: ' + err.message);
      }
    }
  };

  const refreshToken = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      // Llamar al endpoint específico de renovación de token
      const response = await dropboxService.refreshToken();
      
      if (response.success) {
        setConnectionStatus('connected');
        setSuccess('✅ Token renovado exitosamente. Dropbox está listo para usar.');
        
        // Verificar conexión después de renovar
        setTimeout(async () => {
          try {
            await testConnection();
          } catch (err) {
            console.warn('Error verificando conexión después de renovar:', err);
          }
        }, 1000);
      } else {
        setError('❌ No se pudo renovar el token: ' + (response.message || 'Error desconocido'));
      }
    } catch (err) {
      setError('❌ Error al renovar token: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const processOAuthCode = async (code) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      console.log('🔐 Procesando código OAuth automáticamente...');
      
      // Obtener configuración actual para enviar las credenciales correctas
      const config = await dropboxService.getActiveConfig();
      
      if (!config || !config.appKey || !config.appSecret) {
        setError('❌ No se encontró configuración de Dropbox. Por favor configura App Key y App Secret primero.');
        return;
      }
      
      const redirectUri = window.location.origin + window.location.pathname;
      
      const confirmData = {
        appName: config.nombreApp || config.appName || 'conem_folder',
        appKey: config.appKey,
        appSecret: config.appSecret,
        code: code.trim(),
        redirectUri: redirectUri
      };
      
      console.log('📤 Enviando código a backend...');
      console.log('🔙 Redirect URI:', redirectUri);
      
      // Usar el endpoint que acepta configuración completa
      const response = await api.post('/dropbox/confirm-with-config', confirmData);
      
      console.log('📥 Respuesta del servidor:', response.data);
      
      if (response.data && response.data.success) {
        setSuccess('✅ Token OAuth configurado exitosamente de forma automática. Dropbox está listo para usar.');
        setConnectionStatus('connected');
        
        // Recargar configuración
        setTimeout(async () => {
          await loadConfiguration();
        }, 1000);
      } else {
        setError('❌ Error al confirmar la configuración: ' + (response.data?.message || 'Código inválido o expirado'));
      }
    } catch (err) {
      console.error('❌ Error completo:', err);
      if (err.response) {
        const errorMsg = err.response.data?.message || err.response.data?.error || err.response.statusText;
        setError('❌ Error del servidor: ' + errorMsg);
      } else {
        setError('❌ Error al confirmar la configuración: ' + err.message);
      }
    } finally {
      setSaving(false);
      setAutoProcessing(false);
    }
  };

  const renewOAuth = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      console.log('🔄 Iniciando renovación OAuth...');
      
      // Obtener configuración actual
      const config = await dropboxService.getActiveConfig();
      
      if (!config || !config.appKey || !config.appSecret) {
        setError('❌ No hay configuración de Dropbox. Configure primero App Key y App Secret.');
        return;
      }
      
      // Construir URL de autorización con redirect URI
      const redirectUri = window.location.origin + window.location.pathname;
      const authUrl = `https://www.dropbox.com/oauth2/authorize?client_id=${config.appKey}&response_type=code&token_access_type=offline&redirect_uri=${encodeURIComponent(redirectUri)}`;
      
      console.log('📤 URL de autorización generada:', authUrl);
      console.log('🔙 Redirect URI:', redirectUri);
      
      setSuccess('✅ Redirigiendo a Dropbox para autorización. Serás redirigido automáticamente después de autorizar.');
      
      // Redirigir a Dropbox (en la misma ventana para que funcione el redirect)
      setTimeout(() => {
        window.location.href = authUrl;
      }, 1500);
      
    } catch (err) {
      console.error('Error en renovación OAuth:', err);
      setError('❌ Error al iniciar renovación OAuth: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentConfig = async () => {
    try {
      setLoading(true);
      const config = await dropboxService.getConfigWithToken();
      setCurrentConfig(config);
      
      // Pre-llenar TODOS los campos con la configuración actual (INCLUYENDO el token)
      if (config.nombreApp || config.appName) {
        setDropboxConfig(prev => ({
          ...prev,
          id: config.id || prev.id,
          urlToken: config.urlToken || prev.urlToken,
          nombreApp: config.nombreApp || config.appName || prev.nombreApp,
          activo: config.activo !== undefined ? config.activo : prev.activo,
          appKey: config.appKey || prev.appKey,
          appSecret: config.appSecret || prev.appSecret,
          accessToken: config.accessToken || prev.accessToken, // Cargar token en el campo principal
          automaticSave: config.automaticSave !== undefined ? config.automaticSave : prev.automaticSave,
          basePath: config.basePath || '/CONEM_Documents'
        }));
        // También pre-llenar el campo temporal para la sección de "Configurar Token Directo"
        setAccessToken(config.accessToken || '');
        console.log('🔑 Token cargado en campos:', config.accessToken ? `${config.accessToken.length} caracteres` : 'Sin token');
      }
      
      console.log('📋 Configuración actual cargada:', config);
    } catch (error) {
      console.error('Error cargando configuración actual:', error);
      setCurrentConfig(null);
    } finally {
      setLoading(false);
    }
  };

  const updateTokenOnly = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      if (!accessToken.trim()) {
        setError('Por favor ingresa un access token válido');
        return;
      }
      
      console.log('🔄 Actualizando solo el token...');
      const response = await dropboxService.updateTokenOnly(accessToken.trim());
      
      if (response && response.success) {
        setSuccess('✅ Token actualizado exitosamente en BD');
        setShowTokenConfig(false);
        setAccessToken('');
        setCurrentConfig(null);
        setShowCurrentToken(false);
        await loadConfiguration();
      } else {
        setError('❌ Error al actualizar el token: ' + (response.message || 'Error desconocido'));
      }
    } catch (err) {
      console.error('Error actualizando token:', err);
      if (err.response) {
        setError(`❌ Error del servidor: ${err.response.data?.message || err.response.statusText}`);
      } else {
        setError('❌ Error de conexión: ' + err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const configureWithToken = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      if (!accessToken.trim()) {
        setError('Por favor ingresa un access token válido');
        return;
      }
      
      const tokenData = {
        id: dropboxConfig.id,
        urlToken: dropboxConfig.urlToken,
        nombreApp: dropboxConfig.nombreApp,
        activo: dropboxConfig.activo,
        appKey: dropboxConfig.appKey,
        appSecret: dropboxConfig.appSecret,
        automaticSave: dropboxConfig.automaticSave,
        basePath: dropboxConfig.basePath,
        accessToken: accessToken.trim()
      };
      
      // Verificar que tenemos token de autenticación
      const authToken = sessionStorage.getItem('authToken');
      if (!authToken) {
        setError('❌ No hay token de autenticación. Por favor inicia sesión nuevamente.');
        return;
      }
      
      console.log('🔑 Token de autenticación encontrado:', authToken ? 'SÍ' : 'NO');
      console.log('📤 Enviando datos a /dropbox/test-token');
      
      // Usar el nuevo endpoint de prueba
      const response = await api.post('/dropbox/test-token', tokenData);
      const result = response.data;
      
      if (result && result.success) {
        setSuccess('✅ Token configurado exitosamente. Dropbox está listo para usar.');
        setConnectionStatus('connected');
        setShowTokenConfig(false);
        setAccessToken('');
        setCurrentConfig(null);
        setShowCurrentToken(false);
        await loadConfiguration();
      } else {
        setError('❌ Error al configurar el token. Verifica que sea válido.');
      }
    } catch (err) {
      console.error('Error completo al configurar token:', err);
      if (err.response) {
        console.error('Response data:', err.response.data);
        console.error('Response status:', err.response.status);
        setError(`❌ Error del servidor (${err.response.status}): ${err.response.data?.message || err.response.statusText}`);
      } else if (err.request) {
        setError('❌ No se pudo conectar con el servidor. Verifica que el backend esté funcionando.');
      } else {
        setError('❌ Error al configurar token: ' + err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const testUploadCapability = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      // Crear un archivo de prueba pequeño
      const testContent = `Prueba de subida - ${new Date().toISOString()}`;
      const testFile = new Blob([testContent], { type: 'text/plain' });
      const formData = new FormData();
      formData.append('file', testFile, 'test_upload.txt');
      formData.append('path', '/test');
      
      const response = await dropboxService.uploadFile(formData);
      
      if (response.success) {
        setSuccess('✅ Prueba de subida exitosa. Dropbox está listo para recibir archivos de los programas.');
      } else {
        setError(`❌ Error en la prueba de subida: ${response.message || 'Error desconocido'}`);
      }
    } catch (err) {
      console.error('Error completo:', err);
      if (err.response) {
        setError(`❌ Error del servidor: ${err.response.data?.message || err.response.statusText}`);
      } else if (err.request) {
        setError('❌ No se pudo conectar con el servidor. Verifica que el backend esté funcionando.');
      } else {
        setError(`❌ Error al probar subida de archivos: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const saveDropboxSettings = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Preparar datos completos de configuración
      const configData = {
        id: dropboxConfig.id,
        urlToken: dropboxConfig.urlToken,
        nombreApp: dropboxConfig.nombreApp,
        activo: dropboxConfig.activo,
        appKey: dropboxConfig.appKey,
        appSecret: dropboxConfig.appSecret,
        accessToken: dropboxConfig.accessToken, // Incluir access token
        automaticSave: dropboxConfig.automaticSave,
        basePath: dropboxConfig.basePath
      };
      
      console.log('💾 Guardando configuración completa:', configData);
      
      // Usar el servicio para guardar toda la configuración
      const response = await dropboxService.saveFullConfig(configData);
      
      if (response && response.success) {
        // Save to localStorage as fallback
        localStorage.setItem('dropbox_base_path', dropboxConfig.basePath);
        localStorage.setItem('dropbox_auto_save', dropboxConfig.automaticSave);
        
        setSuccess('✅ Configuración de Dropbox guardada exitosamente');
        
        // Recargar configuración para obtener el ID actualizado
        await loadConfiguration();
      } else {
        setError('❌ Error al guardar la configuración: ' + (response?.message || 'Error desconocido'));
      }
    } catch (err) {
      console.error('Error guardando configuración:', err);
      setError('❌ Error al guardar la configuración: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveConfiguration = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      if (!dropboxConfig.appName || !dropboxConfig.appKey || !dropboxConfig.appSecret) {
        setError('Por favor completa todos los campos obligatorios');
        return;
      }
      
      // Save base path and auto sync settings first
      try {
        await dropboxService.updateBasePath(dropboxConfig.basePath);
        await dropboxService.updateAutomaticSave(dropboxConfig.autoSync);
      } catch (err) {
        console.warn('Error saving Dropbox settings:', err);
        // Continue with the rest of the configuration even if these fail
      }
      
      const configData = {
        appName: dropboxConfig.appName,
        appKey: dropboxConfig.appKey,
        appSecret: dropboxConfig.appSecret
      };
      
      const response = await dropboxService.setConfig(configData);
      
      if (response && response.url) {
        setAuthorizationUrl(response.url);
        setConfiguring(false);
        setConfirmation(true);
        setSuccess('Por favor autoriza la aplicación en Dropbox usando el enlace generado');
      } else {
        setError('Error al generar la URL de autorización');
      }
    } catch (err) {
      setError('Error al guardar la configuración: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmConfiguration = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      if (!authorizationCode || !authorizationCode.trim()) {
        setError('Por favor ingresa el código de autorización');
        return;
      }
      
      console.log('🔐 Confirmando código de autorización...');
      console.log('📋 Código recibido:', authorizationCode.substring(0, 10) + '...');
      
      // Obtener configuración actual para enviar las credenciales correctas
      const config = await dropboxService.getActiveConfig();
      
      if (!config || !config.appKey || !config.appSecret) {
        setError('❌ No se encontró configuración de Dropbox. Por favor configura App Key y App Secret primero.');
        return;
      }
      
      const confirmData = {
        appName: config.nombreApp || config.appName || 'conem_folder',
        appKey: config.appKey,
        appSecret: config.appSecret,
        code: authorizationCode.trim()
      };
      
      console.log('📤 Enviando confirmación con credenciales completas');
      
      // Usar el endpoint que acepta configuración completa
      const response = await api.post('/dropbox/confirm-with-config', confirmData);
      
      console.log('📥 Respuesta del servidor:', response.data);
      
      if (response.data && response.data.success) {
        setSuccess('✅ Token OAuth configurado exitosamente. Dropbox está listo para usar.');
        setConfirmation(false);
        setAuthorizationCode('');
        setAuthorizationUrl('');
        setConnectionStatus('connected');
        
        // Recargar configuración
        setTimeout(async () => {
          await loadConfiguration();
        }, 1000);
      } else {
        setError('❌ Error al confirmar la configuración: ' + (response.data?.message || 'Código inválido o expirado'));
      }
    } catch (err) {
      console.error('❌ Error completo:', err);
      if (err.response) {
        const errorMsg = err.response.data?.message || err.response.data?.error || err.response.statusText;
        setError('❌ Error del servidor: ' + errorMsg);
      } else {
        setError('❌ Error al confirmar la configuración: ' + err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const savePipConfiguration = async () => {
    try {
      setSaving(true);
      setError('');
      
      if (!dropboxConfig.pipBasePath || dropboxConfig.pipBasePath.trim() === '') {
        setError('Por favor ingresa una ruta válida para la carpeta base de PIP');
        return;
      }
      
      // Guardar configuración en localStorage por ahora
      localStorage.setItem('conem_pip_base_path', dropboxConfig.pipBasePath);
      
      // También enviar al backend para persistir
      try {
        const response = await fetch('https://crm.conem.com.mx/api/config/pip-path', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            pipBasePath: dropboxConfig.pipBasePath
          })
        });
        
        if (response.ok) {
          setSuccess('Configuración de carpeta PIP guardada exitosamente');
        } else {
          // Si falla el backend, al menos queda en localStorage
          setSuccess('Configuración guardada localmente (verificar conexión con servidor)');
        }
      } catch (backendError) {
        // Si falla el backend, al menos queda en localStorage
        setSuccess('Configuración guardada localmente (verificar conexión con servidor)');
        console.warn('Error guardando en backend:', backendError);
      }
      
    } catch (err) {
      setError('Error al guardar configuración: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const syncNow = async () => {
    try {
      setSaving(true);
      setError('');
      
      const response = await dropboxService.syncFiles();
      
      if (response.success) {
        setSuccess('Sincronización completada exitosamente');
        await loadConfiguration();
      } else {
        setError('Error durante la sincronización');
      }
    } catch (err) {
      setError('Error al sincronizar: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'success';
      case 'connecting': return 'warning';
      default: return 'error';
    }
  };

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected': return 'Conectado';
      case 'connecting': return 'Conectando...';
      default: return 'Desconectado';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Cargando configuración...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '95%',
      maxWidth: '1200px',
      mx: 'auto',
      mt: 2,
      mb: 2,
      background: '#fff',
      borderRadius: 4,
      boxShadow: '0 4px 32px rgba(0,0,0,0.10)',
      p: 4,
    }}>
      {/* Header */}
      <Box sx={{ position: 'relative', mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            letterSpacing: 1,
            color: '#222',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          📦 Configuración de Dropbox
        </Typography>
        <Chip
          label={getConnectionStatusText()}
          color={getConnectionStatusColor()}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            fontWeight: 600,
            fontSize: '0.9rem'
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/* 1. CREDENCIALES DE APLICACIÓN */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: '#1976d2' }}>
            🔐 1. Credenciales de Aplicación Dropbox
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
            Configura las credenciales de tu aplicación de Dropbox para establecer la conexión.
          </Typography>
          
          
          {/* Configuración con Token Directo */}
          {showTokenConfig && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 'bold' }}>
                🔑 Configurar Token de Acceso Directo
              </Typography>
              
              {/* Mostrar configuración actual si existe */}
              {currentConfig && currentConfig.accessToken && (
                <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    📋 Configuración Actual en BD:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>App:</strong> {currentConfig.appName}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Estado:</strong> {currentConfig.active ? '✅ Activa' : '❌ Inactiva'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      <strong>Token:</strong> 
                    </Typography>
                    {showCurrentToken ? (
                      <Typography variant="body2" sx={{ 
                        fontFamily: 'monospace', 
                        fontSize: '0.8rem',
                        wordBreak: 'break-all',
                        bgcolor: '#fff',
                        p: 0.5,
                        borderRadius: 0.5,
                        border: '1px solid #ddd'
                      }}>
                        {currentConfig.accessToken}
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {currentConfig.accessToken.substring(0, 10)}...{currentConfig.accessToken.slice(-4)} 
                        ({currentConfig.accessToken.length} caracteres)
                      </Typography>
                    )}
                    <Button
                      size="small"
                      onClick={() => setShowCurrentToken(!showCurrentToken)}
                      sx={{ ml: 1, minWidth: 'auto', p: 0.5 }}
                    >
                      {showCurrentToken ? '🙈' : '👁️'}
                    </Button>
                  </Box>
                </Box>
              )}
              
              <Typography variant="body2" sx={{ mb: 2 }}>
                {currentConfig?.accessToken ? 'Token actual cargado - puedes editarlo o reemplazarlo:' : 'Pega aquí tu token de acceso de Dropbox:'}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                placeholder={currentConfig?.accessToken ? 'Token actual mostrado - edítalo o reemplázalo' : 'Pega tu access token aquí...'}
                variant="outlined"
                sx={{ mb: 2 }}
                helperText={accessToken ? `Token de ${accessToken.length} caracteres cargado` : ''}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={updateTokenOnly}
                  disabled={saving || !accessToken.trim()}
                  sx={{ 
                    fontWeight: 600, 
                    textTransform: 'none',
                    backgroundColor: '#4caf50',
                    '&:hover': {
                      backgroundColor: '#45a049'
                    }
                  }}
                >
                  {saving ? 'Actualizando...' : 'Actualizar Token'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowTokenConfig(false);
                    setAccessToken('');
                    setCurrentConfig(null);
                    setShowCurrentToken(false);
                  }}
                  sx={{ fontWeight: 600, textTransform: 'none' }}
                >
                  Cancelar
                </Button>
              </Box>
            </Alert>
          )}
          
          {!configuring && !confirmation && (
            <Grid container spacing={3}>
              {/* URL Token */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="URL Token"
                  value={dropboxConfig.urlToken}
                  onChange={(e) => handleConfigChange('urlToken', e.target.value)}
                  variant="outlined"
                  helperText="URL del endpoint de token de Dropbox"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000',
                      fontWeight: 500
                    }
                  }}
                />
              </Grid>
              
              {/* Estado Activo */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={dropboxConfig.activo}
                      onChange={(e) => handleConfigChange('activo', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <SettingsIcon color={dropboxConfig.activo ? 'primary' : 'disabled'} sx={{ mr: 1 }} />
                      <Typography sx={{ color: '#000', fontWeight: 500 }}>
                        Configuración Activa
                      </Typography>
                    </Box>
                  }
                  labelPlacement="start"
                  sx={{ 
                    ml: 0,
                    mt: 2,
                    '& .MuiFormControlLabel-label': { 
                      marginLeft: 1,
                      color: 'text.primary',
                      fontWeight: 500
                    }
                  }}
                />
              </Grid>
              
              {/* Nombre de la Aplicación */}
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Nombre de la Aplicación"
                  value={dropboxConfig.nombreApp}
                  onChange={(e) => handleConfigChange('nombreApp', e.target.value)}
                  variant="outlined"
                  helperText="Nombre identificador de la aplicación"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000',
                      fontWeight: 500
                    }
                  }}
                />
              </Grid>
              
              {/* App Key */}
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="App Key"
                  value={dropboxConfig.appKey}
                  onChange={(e) => handleConfigChange('appKey', e.target.value)}
                  variant="outlined"
                  helperText="Clave de la aplicación Dropbox"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000',
                      fontWeight: 500
                    }
                  }}
                />
              </Grid>
              
              {/* App Secret */}
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="App Secret"
                  type={showAppSecret ? "text" : "password"}
                  value={dropboxConfig.appSecret}
                  onChange={(e) => handleConfigChange('appSecret', e.target.value)}
                  variant="outlined"
                  helperText="Clave secreta de la aplicación"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000',
                      fontWeight: 500
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowAppSecret(!showAppSecret)}
                          edge="end"
                          size="small"
                        >
                          {showAppSecret ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              {/* Alerta de Redirect URI */}
              <Grid size={{ xs: 12 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    ⚙️ Configuración Requerida en Dropbox:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Para que el botón "Renovar OAuth" funcione automáticamente, debes agregar este Redirect URI en tu app de Dropbox:
                  </Typography>
                  <Box sx={{ 
                    bgcolor: '#fff3cd', 
                    p: 1.5, 
                    borderRadius: 1, 
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                    wordBreak: 'break-all'
                  }}>
                    {window.location.origin + window.location.pathname}
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1, fontSize: '0.85rem' }}>
                    📍 Ve a: <strong>https://www.dropbox.com/developers/apps</strong> → Tu App → Settings → Redirect URIs → Add
                  </Typography>
                </Alert>
              </Grid>
              
              {/* Access Token */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Access Token"
                  multiline
                  rows={3}
                  value={dropboxConfig.accessToken}
                  onChange={(e) => handleConfigChange('accessToken', e.target.value)}
                  variant="outlined"
                  helperText="Token de acceso de Dropbox (se carga automáticamente desde la BD)"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000',
                      fontWeight: 500
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SecurityIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              {/* Configuración de Ruta Base y Guardado Automático */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Ruta Base"
                  value={dropboxConfig.basePath || ''}
                  onChange={(e) => handleConfigChange('basePath', e.target.value)}
                  variant="outlined"
                  helperText="Ruta base donde se guardarán los archivos en Dropbox"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000',
                      fontWeight: 500
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FolderIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={dropboxConfig.automaticSave || false}
                      onChange={(e) => handleConfigChange('automaticSave', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <SyncIcon color={dropboxConfig.automaticSave ? 'primary' : 'disabled'} sx={{ mr: 1 }} />
                      <Typography sx={{ color: '#000', fontWeight: 500 }}>
                        Guardado Automático
                      </Typography>
                    </Box>
                  }
                  labelPlacement="start"
                  sx={{ 
                    ml: 0,
                    mt: 2,
                    '& .MuiFormControlLabel-label': { 
                      marginLeft: 1,
                      color: 'text.primary',
                      fontWeight: 500
                    }
                  }}
                />
              </Grid>
              
              {/* Botones para guardar configuración y probar conexión */}
              <Grid size={{ xs: 12 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={saveDropboxSettings}
                  disabled={saving}
                  fullWidth
                  sx={{ 
                    fontWeight: 600, 
                    textTransform: 'none',
                    backgroundColor: '#4caf50',
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: '#45a049'
                    }
                  }}
                >
                  {saving ? 'Guardando...' : '💾 Guardar Credenciales'}
                </Button>
              </Grid>
            </Grid>
          )}

        </CardContent>
      </Card>

      {/* 2. GESTIÓN DE TOKENS */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: '#1976d2' }}>
            🔑 2. Gestión de Tokens de Acceso
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
            Renueva o actualiza los tokens de acceso para mantener la conexión activa.
          </Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Button
                variant="contained"
                startIcon={<CloudSyncIcon />}
                onClick={renewOAuth}
                disabled={loading}
                fullWidth
                sx={{ 
                  fontWeight: 600, 
                  textTransform: 'none',
                  backgroundColor: '#00bcd4',
                  color: '#fff',
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: '#0097a7'
                  },
                  '&:disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#999'
                  }
                }}
              >
                {loading ? 'Generando...' : '🔄 Renovar OAuth (Recomendado)'}
              </Button>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#666' }}>
                Renueva el token usando OAuth. Se abrirá Dropbox para autorizar.
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Button
                variant="outlined"
                startIcon={<SyncIcon />}
                onClick={refreshToken}
                disabled={loading}
                fullWidth
                sx={{ 
                  fontWeight: 600, 
                  textTransform: 'none',
                  borderColor: '#9c27b0',
                  color: '#9c27b0',
                  py: 1.5,
                  '&:hover': {
                    borderColor: '#7b1fa2',
                    backgroundColor: 'rgba(156, 39, 176, 0.04)'
                  },
                  '&:disabled': {
                    borderColor: '#e0e0e0',
                    color: '#999'
                  }
                }}
              >
                {loading ? 'Renovando...' : '♻️ Renovar Token Automático'}
              </Button>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#666' }}>
                Renueva el token automáticamente (requiere refresh token).
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Button
                variant="outlined"
                startIcon={<SecurityIcon />}
                onClick={() => {
                  setShowTokenConfig(true);
                  loadCurrentConfig();
                }}
                disabled={loading}
                fullWidth
                sx={{ 
                  fontWeight: 600, 
                  textTransform: 'none',
                  borderColor: '#f44336',
                  color: '#f44336',
                  py: 1.5,
                  '&:hover': {
                    borderColor: '#d32f2f',
                    backgroundColor: 'rgba(244, 67, 54, 0.04)'
                  },
                  '&:disabled': {
                    borderColor: '#e0e0e0',
                    color: '#999'
                  }
                }}
              >
                🔐 Configurar Token Directo
              </Button>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#666' }}>
                Actualiza el token manualmente pegando un token de acceso.
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 3. PRUEBAS DE CONEXIÓN */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: '#1976d2' }}>
            ✅ 3. Pruebas de Conexión
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
            Verifica que la conexión con Dropbox funciona correctamente.
          </Typography>

          <Alert 
            severity={connectionStatus === 'connected' ? 'success' : 'warning'} 
            sx={{ mb: 3 }}
          >
            <Typography variant="body2">
              <strong>Estado Actual:</strong> {' '}
              {connectionStatus === 'connected' 
                ? '✅ Conectado - Dropbox está listo para recibir archivos'
                : '⚠️ Desconectado - Prueba la conexión para verificar el acceso'
              }
            </Typography>
          </Alert>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Button
                variant="outlined"
                startIcon={<CloudSyncIcon />}
                onClick={testConnection}
                disabled={loading || connectionStatus === 'connecting'}
                fullWidth
                sx={{ 
                  fontWeight: 600, 
                  textTransform: 'none',
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  py: 1.5,
                  '&:hover': {
                    borderColor: '#1565c0',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                  }
                }}
              >
                {connectionStatus === 'connecting' ? 'Probando...' : '🔌 Probar Conexión'}
              </Button>
            </Grid>
            
            <Grid size={{ xs: 12, md: 4 }}>
              <Button
                variant="outlined"
                startIcon={<StorageIcon />}
                onClick={testUploadCapability}
                disabled={loading || connectionStatus !== 'connected'}
                fullWidth
                sx={{ 
                  fontWeight: 600, 
                  textTransform: 'none',
                  borderColor: '#ff9800',
                  color: '#ff9800',
                  py: 1.5,
                  '&:hover': {
                    borderColor: '#f57c00',
                    backgroundColor: 'rgba(255, 152, 0, 0.04)'
                  },
                  '&:disabled': {
                    borderColor: '#e0e0e0',
                    color: '#999'
                  }
                }}
              >
                {loading ? 'Probando...' : '📤 Probar Subida'}
              </Button>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={loadConfiguration}
                disabled={loading}
                fullWidth
                sx={{ 
                  fontWeight: 600, 
                  textTransform: 'none',
                  borderColor: '#2196f3',
                  color: '#2196f3',
                  py: 1.5,
                  '&:hover': {
                    borderColor: '#1976d2',
                    backgroundColor: 'rgba(33, 150, 243, 0.04)'
                  },
                  '&:disabled': {
                    borderColor: '#e0e0e0',
                    color: '#999'
                  }
                }}
              >
                {loading ? 'Cargando...' : '🔄 Recargar desde BD'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {configuring && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Configurando conexión con Dropbox...</Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {confirmation && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ 
              p: 3, 
              bgcolor: '#f0f7ff', 
              borderRadius: 2, 
              border: '2px solid #2196f3'
            }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#1976d2', fontWeight: 700 }}>
                🔐 Paso 2: Ingresa el Código de Autorización
              </Typography>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>¿Ya autorizaste en Dropbox?</strong><br/>
                  Dropbox te mostró un código de acceso. Pégalo en el campo de abajo.
                </Typography>
              </Alert>
              
              <Typography sx={{ mb: 2, fontWeight: 600 }}>
                Si aún no has autorizado, haz clic aquí:
              </Typography>
              <Button
                variant="contained"
                href={authorizationUrl}
                target="_blank"
                sx={{ 
                  mb: 3,
                  backgroundColor: '#0061ff',
                  '&:hover': {
                    backgroundColor: '#0051d5'
                  }
                }}
              >
                🔗 Abrir Dropbox para Autorizar
              </Button>
              
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#1976d2' }}>
                📋 Pega aquí el código que te dio Dropbox:
              </Typography>
              <TextField
                fullWidth
                label="Código de Autorización"
                value={authorizationCode}
                onChange={(e) => setAuthorizationCode(e.target.value)}
                variant="outlined"
                placeholder="Ejemplo: abc123def456..."
                helperText="Copia el código completo que apareció en la página de Dropbox"
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff'
                  }
                }}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={confirmConfiguration}
                  disabled={saving || !authorizationCode}
                  sx={{ 
                    fontWeight: 600, 
                    textTransform: 'none',
                    backgroundColor: '#4caf50',
                    '&:hover': {
                      backgroundColor: '#45a049'
                    }
                  }}
                >
                  {saving ? 'Confirmando...' : 'Confirmar Código'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setConfirmation(false);
                    setAuthorizationCode('');
                    setAuthorizationUrl('');
                  }}
                  sx={{ fontWeight: 600, textTransform: 'none' }}
                >
                  Cancelar
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Panel de Control - Solo si está conectado */}
      {connectionStatus === 'connected' && (
        <>

          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                🔎 Verificación en Validar Campos
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={fieldValidationCheck}
                    disabled={savingFieldValidationCheck}
                    onChange={(e) => handleFieldValidationCheckChange(e.target.checked)}
                  />
                }
                label="Verificar archivos contra Dropbox en Validar Campos"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {fieldValidationCheck
                  ? 'Encendido: cada vez que se abre Validar Campos se confirma en Dropbox que logos, fotos, documentos y constancias sigan existiendo. Es más lento y consume peticiones a Dropbox.'
                  : 'Apagado: Validar Campos confía en lo registrado en el sistema (más rápido). Si alguien borra o mueve un archivo directamente en Dropbox no se detecta, salvo que se use el botón "Verificar contra Dropbox" dentro de Validar Campos.'}
              </Typography>
            </CardContent>
          </Card>

          {/* Configuración de Carpetas PIP */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
                📁 Configuración de Carpetas PIP
              </Typography>
              
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Estructura automática:</strong> Los archivos PIP se organizarán en subcarpetas por nivel de riesgo (Alto/Medio/Bajo) y por ID de empresa.
                </Typography>
              </Alert>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={dropboxConfig.useDropboxPath !== false}
                        onChange={async (e) => {
                          handleConfigChange('useDropboxPath', e.target.checked);
                          
                          // Guardar en backend
                          try {
                            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                            if (token) {
                              const response = await fetch('/api/dropbox/automatic-save', {
                                method: 'PUT',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': `Bearer ${token}`
                                },
                                body: JSON.stringify({
                                  automaticSave: e.target.checked
                                })
                              });
                              
                              if (response.ok) {
                                console.log('Configuración de guardado automático actualizada');
                              }
                            }
                          } catch (error) {
                            console.error('Error actualizando configuración:', error);
                          }
                          
                          // Mantener localStorage como respaldo
                          localStorage.setItem('conem_use_dropbox_path', e.target.checked.toString());
                        }}
                      />
                    }
                    label="Guardar automáticamente en carpeta Dropbox configurada"
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {dropboxConfig.useDropboxPath !== false 
                      ? "Los archivos se guardarán automáticamente en la carpeta Dropbox configurada"
                      : "Se mostrará ventana de descarga para que el usuario elija dónde guardar"
                    }
                  </Typography>
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Carpeta Base para Archivos PIP"
                    value={dropboxConfig.pipBasePath}
                    onChange={(e) => handleConfigChange('pipBasePath', e.target.value)}
                    variant="outlined"
                    placeholder="C:/Users/usuario/Dropbox/CONEM_PIPC"
                    helperText="Ruta donde se guardarán los archivos PIP organizados por riesgo y empresa"
                    disabled={dropboxConfig.useDropboxPath === false}
                    InputProps={{
                      startAdornment: (
                        <FolderIcon sx={{ color: 'action.active', mr: 1 }} />
                      ),
                    }}
                  />
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Estructura real de carpetas en Dropbox:</strong>
                    </Typography>
                    <Button 
                      size="small" 
                      onClick={loadFolderStructure}
                      disabled={loadingStructure}
                      startIcon={loadingStructure ? <CircularProgress size={16} /> : <SyncIcon />}
                    >
                      {loadingStructure ? 'Cargando...' : 'Actualizar'}
                    </Button>
                  </Box>
                  <Box sx={{ 
                    mt: 2, 
                    p: 3, 
                    backgroundColor: '#ffffff', 
                    border: '1px solid #e0e0e0',
                    borderRadius: 2, 
                    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
                    fontSize: '0.9rem',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}>
                    {/* Barra de navegación estilo Windows */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 2, 
                      pb: 1, 
                      borderBottom: '1px solid #e0e0e0',
                      backgroundColor: '#f8f9fa',
                      p: 1,
                      borderRadius: 1
                    }}>
                      <FolderIcon sx={{ color: '#1976d2', mr: 1, fontSize: '1.2rem' }} />
                      <Typography sx={{ fontWeight: 500, color: '#333' }}>
                        📁 {folderStructure.basePath || dropboxConfig.basePath || '/'}
                      </Typography>
                    </Box>
                    
                    {/* Estructura de carpetas REAL */}
                    <Box sx={{ pl: 1 }}>
                      {loadingStructure ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : folderStructure.folders.length === 0 ? (
                        <Typography sx={{ color: '#888', fontStyle: 'italic', textAlign: 'center', p: 2 }}>
                          No hay carpetas en la ruta base o Dropbox no está conectado
                        </Typography>
                      ) : (
                        folderStructure.folders.map((item, index) => (
                          <Box 
                            key={index} 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              mb: 0.5,
                              pl: item.level * 3
                            }}
                          >
                            <Box sx={{ width: 16, textAlign: 'center', mr: 1 }}>
                              {item.isFolder ? '📁' : '📄'}
                            </Box>
                            <Typography sx={{ 
                              color: item.isFolder ? '#333' : '#888',
                              fontWeight: item.isFolder && item.level === 0 ? 500 : 400,
                              fontSize: item.isFolder ? '0.9rem' : '0.85rem'
                            }}>
                              {item.name}
                            </Typography>
                          </Box>
                        ))
                      )}
                    </Box>
                    
                    {/* Información adicional */}
                    <Box sx={{ 
                      mt: 2, 
                      pt: 2, 
                      borderTop: '1px solid #e0e0e0',
                      backgroundColor: '#f8f9fa',
                      p: 2,
                      borderRadius: 1
                    }}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
                        💡 <strong>Organización automática:</strong>
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                        • Los archivos se organizan por nivel de riesgo (Alto, Medio, Bajo)
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                        • Cada empresa tiene su propia carpeta con ID único
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                        • Los nombres de archivo incluyen empresa y fecha de generación
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={savePipConfiguration}
                    disabled={saving}
                    sx={{ 
                      fontWeight: 600, 
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)'
                    }}
                  >
                    {saving ? 'Guardando...' : 'Guardar Configuración PIP'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Configuración de Sincronización */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
                ⚙️ Configuración de Sincronización
              </Typography>
              
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Elementos a sincronizar
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={syncSettings.syncQuotations}
                        onChange={(e) => handleSyncSettingsChange('syncQuotations', e.target.checked)}
                      />
                    }
                    label="Cotizaciones"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={syncSettings.syncReports}
                        onChange={(e) => handleSyncSettingsChange('syncReports', e.target.checked)}
                      />
                    }
                    label="Reportes"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={syncSettings.syncUserDocuments}
                        onChange={(e) => handleSyncSettingsChange('syncUserDocuments', e.target.checked)}
                      />
                    }
                    label="Documentos de usuario"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={syncSettings.syncSystemBackups}
                        onChange={(e) => handleSyncSettingsChange('syncSystemBackups', e.target.checked)}
                      />
                    }
                    label="Respaldos del sistema"
                  />
                </Grid>
                
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Opciones avanzadas
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={syncSettings.compressBeforeSync}
                        onChange={(e) => handleSyncSettingsChange('compressBeforeSync', e.target.checked)}
                      />
                    }
                    label="Comprimir antes de sincronizar"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={syncSettings.deleteLocalAfterSync}
                        onChange={(e) => handleSyncSettingsChange('deleteLocalAfterSync', e.target.checked)}
                      />
                    }
                    label="Eliminar archivos locales después de sincronizar"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Archivos Recientes */}
          {recentFiles && recentFiles.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
                  📁 Archivos Recientes
                </Typography>
                <List>
                  {recentFiles.map((file, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <FolderIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={file.name}
                        secondary={`${file.size} - ${file.uploadDate}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default ConfiguracionDropbox;
